/* eslint-disable no-invalid-this */
/* eslint-disable max-lines-per-function */
import { omit } from "lodash";
import React from "react";
import NumberFormat from "react-number-format";
import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { US_PHONE_MASK } from "utils/phone";
import { MaskedInputControl } from "components/haven-design/form/inputs";
import { isInputValid } from "../form-utils";
import { ValidationSchema } from "../validation-schema";

const socialMask = {
  mask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/],
  placeholder: "xxx-xx-xxxx",
};
const phoneMask = {
  mask: US_PHONE_MASK,
  placeholder: "(555) 123-7698",
};

// remove these from the <input> props
const STRIPPED_PROPS = [
  "formData",
  "onChangeAction",
  "onBlurAction",
  "persistTableName",
  "validationSchema",
  "inputRef",
  "inputStyle",
  "filters",
  "formGroupStyle",
  "style",
  "shrink",
];

type Props = {
  formData: Record<string, string | number | undefined>;
  formGroupStyle?: React.CSSProperties;
  inputRef?: (ref: HTMLInputElement | HTMLTextAreaElement) => void;
  label?: string;
  name: string;
  onBlurAction: (props: Props) => void;
  onChangeAction: (props: Props) => void;
  required?: boolean;
  style?: React.CSSProperties;
  type?: string;
  validationSchema: ValidationSchema[];
  shrink?: boolean
}

type State = {
  errMsg: string;
  hasError: boolean;
}

// Basic Input component that provides support for a
// label, onChange/onBlur actions, and uses a generic formData
// map to fill it's own value if present in map
/**
 * Deprecated, use material for new code
 * */
export class InputBasic extends React.Component<Props & React.HTMLProps<HTMLInputElement>, State> {
  static defaultProps = {
    type: "text",
    onChangeAction () {
      return null;
    },
    onBlurAction () {
      return null;
    },
    formData: {},
    validationSchema: [],
  };

  inputRef: HTMLInputElement | HTMLTextAreaElement| null = null;

  state = {
    hasError: false,
    errMsg: "",
  };

  handleBlur = () => {
    const { formData, name, label, validationSchema } = this.props;
    if (validationSchema.length > 0) {
      const [isValid, errMsg] = isInputValid({
        formData,
        name,
        label,
        validationSchema,
      });

      if (!isValid) {
        this.setState({ hasError: true, errMsg });
      }
    }

    this.props.onBlurAction(this.props);
  };

  componentDidMount () {
    if (!this.props.inputRef || this.inputRef === null) {
      return;
    }

    this.props.inputRef(this.inputRef);
  }

  render () {
    const { name, type, label, formData } = this.props;
    const newProps = omit(this.props, STRIPPED_PROPS);
    const inputErrorStyle = this.state.hasError ? { borderColor: "#c50010" } : {};
    let { defaultValue } = this.props;
    // eslint-disable-next-line no-undefined
    const shouldShrink = this.props.shrink || (defaultValue ? true : undefined);
    let myInput: React.ReactNode | null = null;
    const labelNode = <InputLabel htmlFor={name} variant="outlined" shrink={shouldShrink}>
      {label}
    </InputLabel>;

    if (type === "currency_usd") {
      myInput = (
        <NumberFormat
          isNumericString
          allowNegative={false}
          aria-describedby={`${name}-helper-text`}
          name={name}
          defaultValue={formData[name]}
          id={name}
          // className="form-control"
          thousandSeparator={true}
          prefix={"$"}
          style={{ ...inputErrorStyle, ...this.props.style }}
          getInputRef={(node: HTMLInputElement) => (this.inputRef = node)}
          onBlur={this.handleBlur}
          onFocus={() => this.setState({ hasError: false, errMsg: "" })}
          onValueChange={values => ({
            target: { value: values.value },
          })}
          customInput={OutlinedInput}
          inputProps={{
            label,
            notched: true,
          }}
          label={label}
          notched
        />
      );
    } else if (["phone_number", "ssn"].includes(type || "")) {
      if (type === "phone_number") {
        defaultValue = defaultValue ? `${defaultValue}` : "";
        defaultValue = defaultValue.startsWith("+1") ? defaultValue.slice(2) : defaultValue;
      }
      const selectedMask = type === "ssn" ? socialMask : phoneMask;

      myInput = (
        <OutlinedInput
          aria-describedby={`${name}-helper-text`}
          id={name}
          label={label}
          margin="dense"
          notched={shouldShrink}
          inputComponent={MaskedInputControl}
          inputProps={{
            mask: selectedMask.mask,
            name,
            placeholder: selectedMask.placeholder,
          }}
          onBlur={this.handleBlur}
          inputRef={(node: any) => (this.inputRef = node)}
          onChange={this.props.onChangeAction.bind(null, this.props)}
          style={{ ...inputErrorStyle, ...this.props.style }}
          defaultValue={defaultValue}
          type="text"
        />);
    } else {
      myInput = (
        <OutlinedInput
          aria-describedby={`${name}-helper-text`}
          type={type}
          name={name}
          id={name}
          inputRef={(node: any) => (this.inputRef = node)}
          // @ts-ignore
          onChange={this.props.onChangeAction.bind(null, this.props)}
          onBlur={this.handleBlur}
          onFocus={() => this.setState({ hasError: false, errMsg: "" })}
          {...newProps}
        />
      );
    }

    return (
      <FormControl
        variant="outlined"
        error={this.state.hasError}
        fullWidth
        margin="dense"
      >
        {labelNode}
        {myInput}
        {this.state.hasError && (
          <FormHelperText id={`${name}-helper-text`} variant="outlined">
            {this.state.errMsg}
          </FormHelperText>)}
      </FormControl>);
  }
}
